import { datasources, getDatabase } from "./database/datasources";
import websocketEngine from "./websocket.engine";
import {
    getUserDetails,
    getMessageLangs,
    getMessageTypes,
    getAccidentTypes,
    getPahtologies,
    getActivities,
    getInternalOverlayTypes,
    getOverlayServices,
    getMapServices,
    getEvents,
    getEventOverlayType,
    getHelicopterPartners,
    getGeoIP,
    getSms
} from '../api';
import { Storage, STORAGE_KEYS } from "../utils/store";

class GendLocApp {
    #websocketEngine = websocketEngine;
    #databases = {}
    #initDb;

    static #instance;
    
    constructor() {
        if (GendLocApp.#instance) {
            return GendLocApp.#instance;
        }
        
        GendLocApp.#instance = this;

        this.#initDb = getDatabase(datasources.eventDb)
            .then(db => {
                this.#databases.eventDb = db;
                return Promise.all([
                    this.#cleanEventFromLocalDb(),
                    this.#initEventOfTheDay(),
                    this.#initHelicopters(),
                    this.#initSms()
                ]);
            })
            .catch(err => console.error('eventDb', err));
    }

    init() {
        return Promise.all([
            this.#fetchStaticData(),
            this.#initDb
        ]);
    }

    registerWs(name, url) {
        this.#websocketEngine.connect(name, url);
    }

    unregisterWs(name) {
        this.#websocketEngine.close(name);
    }

    onWsMessage(name, callback) {
        this.#websocketEngine.on(name, callback);
    }

    getDatabase(name) {
        return this.#databases[name];
    }

    #fetchStaticData() {
        if (Storage.get(STORAGE_KEYS.APP_INITIALIZED) && Storage.get(STORAGE_KEYS.ACTIVITY)) {
            return Promise.resolve();
        }

        return Promise.all([
            getUserDetails().then(data => ({name: STORAGE_KEYS.USER_DETAILS, data})),
            getMessageLangs().then(data => ({name: STORAGE_KEYS.MESSAGE_LANGS, data})),
            getMessageTypes().then(data => ({name: STORAGE_KEYS.MESSAGE_TYPES, data})),
            getAccidentTypes().then(data => ({name: STORAGE_KEYS.ACCIDENT_TYPE, data})),
            getPahtologies().then(data => ({name: STORAGE_KEYS.PATHOLOGY, data})),
            getActivities().then(data => ({name: STORAGE_KEYS.ACTIVITY, data})),
            getInternalOverlayTypes().then(data => ({name: STORAGE_KEYS.OVERLAY_INT_TYPES, data})),
            getOverlayServices().then(data => ({name: STORAGE_KEYS.OVERLAY_SRV, data})),
            getMapServices().then(data => ({name: STORAGE_KEYS.MAP_SRV, data})),
            getEventOverlayType().then(data => ({name: STORAGE_KEYS.EVENT_OVERLAY_TYPE, data})),
            getHelicopterPartners().then(data => ({name: STORAGE_KEYS.HELICO, data})),
            getGeoIP().then(data => ({name: STORAGE_KEYS.GEOIP, data}))
        ]).then(dataToStore => {
            dataToStore.forEach(({name, data}) => Storage.store(name, data));
            Storage.store(STORAGE_KEYS.APP_INITIALIZED, true);
        });
    }

    #cleanEventFromLocalDb() {
        const db = this.#databases.eventDb.detailed_event;
        
        return db.getAll()
            .then(events => Promise.all(
                events.filter(e => !e.open)
                .map(e => db.delete(e.id))
            ));
    }

    #initEventOfTheDay() {
        const date = Storage.get(STORAGE_KEYS.TODAY);
        const user = Storage.get(STORAGE_KEYS.USER)?.id;
        const lastUser = Storage.get(STORAGE_KEYS.LAST_USER);
        const expectedSquad = Storage.get(STORAGE_KEYS.USER)?.unit?.code;
        const squad = Storage.get(STORAGE_KEYS.squad);

        const today = (new Date()).toISOString().split('T')[0];

        if (date === today && squad === expectedSquad && user === lastUser) { return; }

        const db = this.#databases.eventDb;
        
        return Promise.all([
            db.sms.clear(),
            db.event.clear()
        ])
            .then(() => getEvents(today))
            .then(({ features }) => {
                features.forEach(db.event.put.bind(db));
                Storage.store(STORAGE_KEYS.TODAY, today);
                Storage.store(STORAGE_KEYS.squad, expectedSquad);
                Storage.store(STORAGE_KEYS.LAST_USER, user);
            });
    }

    #initHelicopters() {
        const db = this.#databases.eventDb.helicopter;
        return getHelicopterPartners()
            .then(helicopters => {
                if (!Array.isArray(helicopters)) {
                    console.error('getHelicopterPartners: helicopters is not an array', helicopters);
                    return;
                }
                return db.clear()
                    .then(() => Promise.all(helicopters.map(h => db.put(h))));
            });
    }

    #initSms() {
        const db = this.#databases.eventDb.sms;
        return getSms()
            .then(smss => {
                if (!Array.isArray(smss)) {
                    console.error('getSms: smss is not an array', smss);
                    return;
                }
                return db.clear()
                    .then(() => Promise.all(smss.map(sms => db.put(sms))));
            });
    }
}

const startApp = () => { 
    const app = new GendLocApp();

    return app.init().then(() => app);
};

export default startApp;